import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { APIurl } from "../APIClient";
import {
  useAuthenticate,
  useEncryptedToggle,
  useGetOrganizationId,
} from "../cacheQueries";
import {
  fetchRecentEncrypted,
  type EncryptedDistanceFilter,
} from "../api/encryptedDetectionClient";
import type { EncryptedDetection } from "../interfaces/EncryptedDetection";
import { normalizeEncryptedDetection } from "../utils/normalizeEncryptedDetection";
import {
  encryptedAllTimeKey,
  encryptedPeakKey,
  encryptedRefinedTimelineKeyRoot,
  encryptedTimelineKey,
  recentEncryptedKey,
  todaysEncryptedKey,
} from "../hooks/useEncryptedScannedDevices";
import { useEncryptedRangePreference } from "../hooks/useEncryptedRangePreference";
import { useUtcDay } from "../hooks/useUtcDay";
import {
  applyRecentEncryptedToCaches,
  hasCachedEncryptedList,
  markEncryptedSessionClosed,
} from "../utils/applyRecentEncrypted";
import { useActiveEncryptedDialogStore } from "../stores/activeEncryptedDialogStore";
import { createCoalescedTrigger } from "./coalescedTrigger";
import {
  createEncryptedSessionReader,
  shouldOpenEncryptedSessionStream,
} from "./encryptedSessionEvents";

const SSE_URL = `${APIurl}events/encrypted`;

/** Events arrive at ~1/s per live session; one read serves all of them. */
const REFRESH_INTERVAL_MS = 1000;

/** Everything an event handler needs to address this org's caches. */
interface CacheParams {
  filter: EncryptedDistanceFilter;
  utcDay: string;
}

/**
 * Keeps the intrusion log's encrypted rows current while sessions are still
 * flying: an encrypted session is now written to MySQL seconds after detection
 * and its `stop` keeps moving, so a list fetched once goes stale immediately.
 *
 * Mounted once at the app root, beside RemarksSseListener.
 */
export default function EncryptedSessionSseListener() {
  const { data: user, isLoading: userLoading } = useAuthenticate();
  const { data: organizationId } = useGetOrganizationId();
  const { data: encryptedToggleData } = useEncryptedToggle();
  const { filter } = useEncryptedRangePreference();
  const utcDay = useUtcDay();
  const queryClient = useQueryClient();

  // The range filter and the UTC day address the caches but say nothing about
  // the stream, so they must not be effect dependencies — reopening the
  // EventSource because the user moved a slider is pure churn. Mirror them and
  // let the handlers read whatever is current when an event lands.
  const paramsRef = useRef<CacheParams>({ filter, utcDay });
  useEffect(() => {
    paramsRef.current = { filter, utcDay };
  });

  useEffect(() => {
    const shouldConnect = shouldOpenEncryptedSessionStream({
      userLoading,
      user,
      organizationId,
      encryptedToggle: encryptedToggleData,
    });
    if (!shouldConnect) return;

    // Set by the cleanup: a read already in flight must not land in the caches
    // of an org, range or toggle state this listener no longer belongs to.
    let disposed = false;

    /** The lists the intrusion log renders. Exact keys — never a prefix. */
    const listKeysFor = (params: CacheParams) => [
      todaysEncryptedKey(organizationId, params.utcDay, params.filter),
      encryptedAllTimeKey(organizationId, params.filter),
    ];

    const refresh = async () => {
      // One snapshot for the whole read: rows fetched with a given range must
      // land in the caches keyed with that same range.
      const params = paramsRef.current;
      const keys = listKeysFor(params);
      // Nothing has fetched these lists (the user is on another page), and
      // applyRecentEncryptedToCaches refuses to seed them — so this read would
      // go straight in the bin. Once a second, on every page.
      if (!hasCachedEncryptedList(queryClient, keys)) return;
      try {
        const rows = await queryClient.fetchQuery({
          queryKey: recentEncryptedKey(organizationId, params.filter),
          queryFn: async () => {
            const detections: EncryptedDetection[] = await fetchRecentEncrypted(
              params.filter,
            );
            return detections.map(normalizeEncryptedDetection);
          },
          staleTime: 0,
        });
        if (disposed) return;
        applyRecentEncryptedToCaches(queryClient, keys, rows);
      } catch {
        // A failed read must NOT reach the caches: the merge treats the
        // incoming list as authoritative, so an empty one would end every
        // live row. The next event retries within a second.
      }
    };

    const trigger = createCoalescedTrigger({
      intervalMs: REFRESH_INTERVAL_MS,
      run: () => void refresh(),
    });

    const reader = createEncryptedSessionReader({
      onActivity: () => trigger.request(),

      // The badge goes out on the event, not when the coalesced read lands.
      onClosed: (id) => {
        const params = paramsRef.current;
        markEncryptedSessionClosed(
          queryClient,
          [
            ...listKeysFor(params),
            recentEncryptedKey(organizationId, params.filter),
          ],
          id,
        );
      },

      // Per-id detail queries are expensive and only the open dialog reads
      // them. The refined timeline is keyed by the group's member ids, which
      // the event does not carry — hence the prefix. cancelRefetch:false: at
      // one event a second, the default would cancel and restart each refetch
      // before it could ever settle.
      onOpenSessionChanged: (id) => {
        void queryClient.invalidateQueries(
          { queryKey: encryptedTimelineKey(id) },
          { cancelRefetch: false },
        );
        void queryClient.invalidateQueries(
          { queryKey: encryptedPeakKey(id) },
          { cancelRefetch: false },
        );
        void queryClient.invalidateQueries(
          { queryKey: encryptedRefinedTimelineKeyRoot },
          { cancelRefetch: false },
        );
      },

      openSessionId: () => useActiveEncryptedDialogStore.getState().id,
    });

    const es = new EventSource(SSE_URL, { withCredentials: true });

    es.onopen = () => {
      // A (re)connect may replay the server's buffer, and a restarted server
      // may even number it from scratch; accept it all.
      reader.reset();
      // One read per connection, because we cannot know what we missed.
      trigger.request();
    };

    const onEncryptedSession = (event: Event) => {
      if (!(event instanceof MessageEvent)) return;
      reader.read({ data: event.data, lastEventId: event.lastEventId });
    };

    es.addEventListener("encrypted_session", onEncryptedSession);

    // Nothing to do but say so: EventSource retries a dropped connection on its
    // own, and a non-200 closes it for good (spec) — reconnect logic here would
    // only fight it. Logged once because an error fires on every retry, and at
    // one event a second a chatty stream would drown the console.
    let loggedStreamError = false;
    es.onerror = () => {
      if (loggedStreamError) return;
      loggedStreamError = true;
      console.warn(
        "[encryptedSse] stream error; readyState =",
        es.readyState,
        es.readyState === EventSource.CLOSED
          ? "(closed — the browser will not retry)"
          : "(the browser will retry)",
      );
    };

    return () => {
      disposed = true;
      es.removeEventListener("encrypted_session", onEncryptedSession);
      // Before close(): a queued read would otherwise fire for the org, range
      // or day this listener is being torn down for.
      trigger.cancel();
      try {
        es.close();
      } catch {
        // ignore
      }
    };
  }, [encryptedToggleData, organizationId, queryClient, user, userLoading]);

  return null;
}
